import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';

/**
 * Static Cookie Policy page for AIgnyx. Matches the visual style of PrivacyPage.
 * AIgnyx uses only strictly-necessary functional cookies (auth session) — no
 * tracking, analytics, or advertising cookies.
 */

const H2 = ({ id, children }) => (
  <h2
    id={id}
    className="font-heading text-2xl sm:text-3xl font-bold mt-14 mb-4 scroll-mt-24"
  >
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="text-sm sm:text-base leading-relaxed text-muted-foreground mb-4">
    {children}
  </p>
);

const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-muted-foreground mb-4 leading-relaxed">
    {children}
  </ul>
);

const A = ({ href, children }) => {
  const isInternal = href.startsWith('/') || href.startsWith('#');
  if (isInternal && href.startsWith('/')) {
    return (
      <Link to={href} className="text-primary hover:underline">
        {children}
      </Link>
    );
  }
  if (isInternal) {
    return (
      <a href={href} className="text-primary hover:underline">
        {children}
      </a>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:underline break-words"
    >
      {children}
    </a>
  );
};

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="cookies-page">
      <div className="noise-overlay" />

      {/* Sticky header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="cookies-back-home">
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Back home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-sm">AIgnyx</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <motion.article
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <p className="text-xs text-muted-foreground mb-2">
            Last updated August 24, 2026
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6">
            Cookie Policy
          </h1>

          <P>
            This Cookie Policy explains how <strong>AIgnyx</strong> (operated by Венета
            Димитрова Кирилова) uses cookies and similar technologies when you visit{' '}
            <A href="https://aignyx.com">https://aignyx.com</A>. It should be read
            alongside our <A href="/privacy">Privacy Policy</A>.
          </P>
          <P>
            <strong>Short version:</strong> AIgnyx uses only strictly-necessary
            functional cookies to keep you logged in and remember your preferences. We do
            not use tracking, analytics, or advertising cookies.
          </P>

          <H2 id="what">1. What Are Cookies?</H2>
          <P>
            Cookies are small text files that a website stores on your device (computer,
            tablet, or phone) when you visit it. They are widely used to make websites
            work efficiently, remember user choices, and provide a smoother experience.
            Similar technologies — like <em>localStorage</em>, <em>sessionStorage</em>,
            and browser cache — behave in comparable ways and are covered by this
            Policy.
          </P>

          <H2 id="use">2. How AIgnyx Uses Cookies</H2>
          <P>
            We keep our cookie usage minimal and deliberate. AIgnyx only stores what is
            required to run the product securely. Specifically, we use:
          </P>
          <UL>
            <li>
              <strong>Authentication session token</strong> — stores your JWT so you
              stay logged in between page loads. Cleared when you log out.
            </li>
            <li>
              <strong>Theme preference</strong> — remembers your choice between light
              and dark mode.
            </li>
            <li>
              <strong>Language preference</strong> — remembers your selected UI language
              (EN, ES, DE, BG).
            </li>
            <li>
              <strong>Cookie banner acknowledgment</strong> — records that you have seen
              and dismissed the consent banner so it does not reappear on every page.
            </li>
          </UL>
          <P>
            All of the above are classified as <strong>strictly-necessary / functional</strong>{' '}
            under the ePrivacy Directive and GDPR. They do not require prior consent
            because the Service cannot function without them.
          </P>

          <H2 id="dont-use">3. What We Do NOT Use</H2>
          <P>AIgnyx does not set, and does not knowingly allow third parties to set:</P>
          <UL>
            <li>Advertising or targeted-marketing cookies</li>
            <li>Cross-site tracking pixels or web beacons</li>
            <li>Behavioural analytics (e.g. Google Analytics, Meta Pixel, Hotjar)</li>
            <li>Social-media &quot;like&quot; or share trackers</li>
            <li>Session-replay or heatmap recording</li>
          </UL>
          <P>
            If this ever changes, we will update this Cookie Policy and re-prompt you
            for consent through the banner before any non-essential cookie is placed.
          </P>

          <H2 id="third-party">4. Third-Party Services</H2>
          <P>
            When you make a payment, AIgnyx redirects you to <strong>Stripe</strong> to
            complete the transaction. Stripe may set its own cookies during checkout,
            which are governed by{' '}
            <A href="https://stripe.com/privacy">Stripe&apos;s Privacy Policy</A>. We do
            not receive or store your card details.
          </P>
          <P>
            AI generation is performed via our provider (OpenAI). This is a
            server-to-server call and does not place any cookies in your browser.
          </P>

          <H2 id="control">5. How to Control or Delete Cookies</H2>
          <P>
            Because our cookies are strictly necessary, disabling them will prevent you
            from staying logged in or using core features. That said, you are always in
            control of what your browser stores:
          </P>
          <UL>
            <li>
              <strong>Log out</strong> — clears the session token stored by AIgnyx.
            </li>
            <li>
              <strong>Browser settings</strong> — every modern browser lets you view,
              block, or delete cookies for a specific site. See the help pages for{' '}
              <A href="https://support.google.com/chrome/answer/95647">Chrome</A>,{' '}
              <A href="https://support.mozilla.org/kb/cookies-information-websites-store-on-your-computer">
                Firefox
              </A>
              ,{' '}
              <A href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac">
                Safari
              </A>
              , or{' '}
              <A href="https://support.microsoft.com/microsoft-edge">Edge</A>.
            </li>
            <li>
              <strong>Private / Incognito mode</strong> — most browsers clear all
              cookies when the window closes.
            </li>
          </UL>

          <H2 id="dnt">6. Do-Not-Track Signals</H2>
          <P>
            No uniform industry standard for handling Do-Not-Track (DNT) browser signals
            has been finalised. Because AIgnyx does not track you across sites anyway,
            our behaviour is the same whether or not you send a DNT signal.
          </P>

          <H2 id="changes">7. Changes to This Cookie Policy</H2>
          <P>
            We may update this Cookie Policy to reflect changes in law or in our
            practices. The &quot;Last updated&quot; date at the top will always show the
            latest revision. If we ever introduce non-essential cookies, we will notify
            you and ask for consent before setting them.
          </P>

          <H2 id="contact">8. Contact Us</H2>
          <P>
            If you have questions about this Cookie Policy or how AIgnyx handles your
            data, please email us at{' '}
            <A href="mailto:hello@aignyx.com">hello@aignyx.com</A> or write to:
          </P>
          <div className="text-sm sm:text-base text-muted-foreground mb-4 leading-relaxed">
            <p>Veneta Kirilova</p>
            <p>Kozloduy 3320</p>
            <p>Bulgaria</p>
          </div>

          {/* Footer */}
          <div className="mt-16 pt-6 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
            <span>© 2026 AIgnyx</span>
            <div className="flex gap-4">
              <Link to="/privacy" className="hover:text-primary">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-primary">
                Terms
              </Link>
              <Link to="/cookies" className="hover:text-primary">
                Cookies
              </Link>
            </div>
          </div>
        </motion.article>
      </main>
    </div>
  );
}
